<template>
  <form ref="form">
    <!-- input event title -->
    <b-field :label="$t('eventTitle')">
      <b-input
        ref="nameInput"
        v-model="name"
        autocomplete="off"
        required
        validation-message="Bitte tragen Sie hier einen Namen für das Event ein"
      />
    </b-field>
    <!-- inout event description -->
    <b-field :label="$t('eventDescription')">
      <MarkdownEditor v-model="description" />
    </b-field>
    <!-- explanation of event type and select fields -->
    <b-field :label="$t('eventType')" />
    <b-message class="is-primary">
      <span v-html="isSubscriptionTrialRunning ? $t('trialSubsEventExplained') : $t('isVideoEventExplained')"></span>
    </b-message>
    <b-field group-multiline grouped>
      <b-radio-button
        v-if="!isSubscriptionTrialRunning"
        v-model="eventType"
        :disabled="!editable"
        native-value="video"
        type="is-primary"
      >
        <b-icon icon="video" />
        <span>{{ $t("eventType-Video") }}</span>
      </b-radio-button>

      <!-- currently disabled becuase audio events aren't working atm -->
      <!-- <b-radio-button
        v-model="eventType"
        :disabled="!editable"
        native-value="audio"
        type="is-primary"
      >
        <b-icon icon="microphone" />
        <span>{{ $t("eventType-Audio") }}</span>
      </b-radio-button> -->

      <b-radio-button
        v-if="!isUndefined(livestreamType) && !isSubscriptionTrialRunning"
        v-model="eventType"
        :disabled="!editable"
        native-value="livestream"
        type="is-primary"
      >
        <b-icon icon="video-wireless" />
        <span>{{ $t("eventType-Livestream") }}</span>
      </b-radio-button>

      <b-radio-button
        v-if="!isUndefined(hybridType)"
        v-model="eventType"
        :disabled="!editable"
        native-value="hybrid"
        type="is-primary"
      >
        <b-icon icon="video-switch" />
        <span>{{ $t("eventType-Hybrid") }}</span>
      </b-radio-button>

      <b-radio-button v-model="eventType" :disabled="!editable" native-value="external">
        <b-icon icon="open-in-new" />
        <b-tooltip multilined class="is-hidden-desktop" :label="$t('eventType-External')" type="is-primary">
          <span>{{ $t("eventType-External-Short") }}</span>
        </b-tooltip>
        <span class="is-hidden-mobile">{{ $t("eventType-External") }}</span>
      </b-radio-button>
    </b-field>

    <!-- only show if eventType is external-->
    <!-- input of external event link -->
    <b-field v-if="eventType == 'external'" :label="$t('externalEventLink')" class="mb-4">
      <b-input v-model="externalEventLink" autocomplete="off" />
    </b-field>

    <!-- only shown if eventType is hybrid -->
    <!-- hybrid event explanation and select boxes -->
    <div v-if="eventType == 'hybrid'">
      <b-field :label="$t('hybridType')" />
      <b-message class="is-primary">
        <span v-html="$t('isHybridExplained')"></span>
      </b-message>
      <b-field group-multiline grouped class="mb-1">
        <b-radio-button
          v-if="!isSubscriptionTrialRunning"
          v-model="hybridType"
          :disabled="!editable"
          native-value="livestream"
          type="is-primary"
        >
          <b-icon icon="video-wireless" />
          <span>{{ $t("eventType-Livestream") }}</span>
        </b-radio-button>
        <b-radio-button v-model="hybridType" :disabled="!editable" native-value="conference" type="is-primary">
          <b-icon icon="account-group" />
          <span>{{ $t("hybridType-Conference") }}</span>
        </b-radio-button>
        <b-radio-button v-model="hybridType" :disabled="!editable" native-value="audiotrack" type="is-primary">
          <b-icon icon="text-to-speech" />
          <span>{{ $t("hybridType-Audiotrack") }}</span>
        </b-radio-button>
      </b-field>
    </div>

    <!-- only shown if eventType livestream or eventType hybrid and hybridType livestream -->
    <!-- livestreamType exlplanation and select boxes -->
    <div
      v-if="
        !isSubscriptionTrialRunning &&
        (eventType == 'livestream' || (eventType == 'hybrid' && hybridType == 'livestream'))
      "
    >
      <b-field :label="$t('livestreamType')" />
      <b-message class="is-primary">
        <span v-html="$t('isLivestreamExplained')"></span>
      </b-message>
      <b-field group-multiline grouped class="mb-1">
        <b-radio-button v-model="livestreamType" :disabled="!editable" native-value="player" type="is-primary">
          <b-icon icon="television-play" />
          <span>{{ $t("livestreamType-Player") }}</span>
        </b-radio-button>
        <b-radio-button v-model="livestreamType" :disabled="!editable" native-value="target" type="is-primary">
          <b-icon icon="upload-network" />
          <span>{{ $t("livestreamType-Target") }}</span>
        </b-radio-button>
        <b-radio-button v-model="livestreamType" :disabled="!editable" native-value="pull" type="is-primary">
          <b-icon icon="download-network" />
          <span>{{ $t("livestreamType-Pull") }}</span>
        </b-radio-button>
      </b-field>
    </div>

    <!-- particpants explained and input -->
    <div v-if="showParticipants()" class="mb-5">
      <b-field :label="isSubscriptionTrialRunning ? $t('trialMaxParticipants') : $t('maxParticipants')" />
      <b-message
        v-if="
          (eventType === 'livestream' && livestreamType === 'player') ||
          (eventType === 'hybrid' && hybridType === 'livestream' && livestreamType === 'player')
        "
        type="is-primary"
      >
        {{ $t("maxParticipantsExplained-Player") }}
      </b-message>
      <b-message v-else-if="eventType === 'hybrid' && hybridType === 'conference'" type="is-primary">
        {{ $t("maxParticipantsExplained-Conference") }}
      </b-message>
      <b-message v-else type="is-primary">
        {{ $t("maxParticipantsExplained") }}
      </b-message>
      <b-field class="mb-6">
        <b-numberinput
          ref="maxParticipantsInput"
          v-model="maxParticipants"
          :min="minParticipantsLimit"
          :max="maxParticipantsLimit"
          :editable="editable"
          :controls="editable"
          type="is-primary"
          expanded
        />
      </b-field>
    </div>

    <!-- only shown if eventType hybrid -->
    <!-- interpretingType explanation and select boxes -->
    <div v-if="eventType == 'hybrid'">
      <b-field :label="$t('interpretingType')" />
      <b-message class="is-primary">
        <span v-html="$t('isInterpretingExplained')"></span>
      </b-message>
      <b-field group-multiline grouped class="mb-1">
        <b-radio-button v-model="interpretingType" :disabled="!editable" native-value="device" type="is-primary">
          <b-icon icon="cellphone-link" />
          <span>{{ $t("interpretingType-Device") }}</span>
        </b-radio-button>
        <b-radio-button v-model="interpretingType" :disabled="!editable" native-value="existing" type="is-primary">
          <b-icon icon="audio-video" />
          <span>{{ $t("interpretingType-Existing") }}</span>
        </b-radio-button>
      </b-field>
      <!-- show if event type is hybrid and interpreting type is device -->
      <div v-if="interpretingType === 'device'" class="mb-5">
        <b-field :label="$t('interpretingParticipants')" />
        <b-field class="mb-6">
          <b-numberinput
            ref="interpretingParticipantsInput"
            v-model="interpretingParticipants"
            :min="2"
            :max="10000"
            :editable="editable"
            :controls="editable"
            type="is-primary"
            expanded
          />
        </b-field>
      </div>
    </div>

    <!-- event password -- currently disabeld -->
    <!-- <b-switch v-model="enablePassword" class="my-3 mt-6">
      <b-field :label="$t('moderationPassword')" />
    </b-switch>
    <b-message class="is-primary">
      {{ $t("moderationPasswordExplained") }}
    </b-message>
    <b-field>
      <b-input
        v-model="moderationPassword"
        expanded
        :disabled="!enablePassword"
      />
    </b-field> -->

    <!-- only shown if eventType is audio -->
    <!-- eventType audio explanation and select boxes -->
    <b-field
      v-if="eventType !== 'livestream' && eventType !== 'external' && eventType !== 'hybrid'"
      :label="$t('allowedToSpeak')"
      class="mt-3"
    />
    <b-field
      v-if="eventType !== 'livestream' && eventType !== 'external' && eventType !== 'hybrid'"
      class="mb-1"
      group-multiline
      grouped
    >
      <b-radio-button v-model="reqToSpeakMode" :native-value="true" type="is-primary">
        <b-icon icon="lock" />
        <span>{{ $t("reqToSpeakMode-true") }}</span>
      </b-radio-button>

      <b-radio-button v-model="reqToSpeakMode" :native-value="false" type="is-primary">
        <b-icon icon="lock-open-variant" />
        <span>{{ $t("reqToSpeakMode-false") }}</span>
      </b-radio-button>
    </b-field>
  </form>
</template>

<script>
import MarkdownEditor from "../MarkdownEditor";
import { db } from "@/firebase";
import isTrialRunning from "./utils";

export default {
  name: "GeneralInfo",
  components: {
    MarkdownEditor,
  },
  props: {
    editable: {
      type: Boolean,
      default: true,
    },
    valid: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      enablePassword: false,
      minParticipantsLimit: 2,
      organizationDoc: {},
    };
  },
  firestore() {
    return {
      organizationDoc: db.doc(`organizations/${this.$route.params.organizationId}`),
    };
  },
  computed: {
    /**
     * Computed property returns organizations collection from firebase db.
     */
    organizationCollection() {
      return db.collection(`organizations`);
    },
    isSubscriptionTrialRunning() {
      const organizationDoc = JSON.parse(JSON.stringify(this.organizationDoc));

      return isTrialRunning(organizationDoc);
    },
    name: {
      get() {
        return this.$store.state.draft.name;
      },
      set(value) {
        this.$store.commit("SET_DRAFT_FIELD", ["name", value]);
      },
    },
    description: {
      get() {
        return this.$store.state.draft.description;
      },
      set(value) {
        this.$store.commit("SET_DRAFT_FIELD", ["description", value]);
      },
    },
    isVideoEvent: {
      get() {
        return this.$store.state.draft.isVideoEvent;
      },
      set(value) {
        this.$store.commit("SET_DRAFT_FIELD", ["isVideoEvent", value]);
      },
    },
    externalEventLink: {
      get() {
        return this.$store.state.draft.externalEventLink;
      },
      set(value) {
        this.$store.commit("SET_DRAFT_FIELD", ["externalEventLink", value]);
      },
    },
    moderationPassword: {
      get() {
        return this.$store.state.moderationPassword;
      },
      set(value) {
        this.$store.commit("SET_MODERATION_PW", value);
      },
    },
    reqToSpeakMode: {
      get() {
        return this.$store.state.draft.reqToSpeakMode || false;
      },
      set(value) {
        this.$store.commit("SET_DRAFT_FIELD", ["reqToSpeakMode", value]);
      },
    },
    eventType: {
      get() {
        return this.$store.state.draft.eventType;
      },
      set(value) {
        if (value == "external" || value == "audio") {
          this.isVideoEvent = false;
        } else {
          this.isVideoEvent = true;
        }
        this.$store.commit("SET_DRAFT_FIELD", ["eventType", value]);
        this.resetSubEventTypes();
      },
    },
    livestreamType: {
      get() {
        return this.$store.state.draft.livestreamType;
      },
      set(value) {
        this.$store.commit("SET_DRAFT_FIELD", ["livestreamType", value]);
      },
    },
    hybridType: {
      get() {
        return this.$store.state.draft.hybridType;
      },
      set(value) {
        this.$store.commit("SET_DRAFT_FIELD", ["hybridType", value]);
      },
    },
    interpretingType: {
      get() {
        return this.$store.state.draft.interpretingType;
      },
      set(value) {
        this.$store.commit("SET_DRAFT_FIELD", ["interpretingType", value]);
      },
    },
    interpretingParticipants: {
      get() {
        return this.$store.state.draft.interpretingParticipants;
      },
      set(value) {
        this.$store.commit("SET_DRAFT_FIELD", ["interpretingParticipants", value]);
      },
    },
    maxParticipants: {
      get() {
        return this.$store.state.draft.maxParticipants;
      },
      set(value) {
        const limitedVal = value > this.maxParticipantsLimit ? this.maxParticipantsLimit : value;
        this.$store.commit("SET_DRAFT_FIELD", ["maxParticipants", limitedVal]);
      },
    },
    maxParticipantsLimit() {
      const organizationDoc = JSON.parse(JSON.stringify(this.organizationDoc));
      // Condition for trial subscription
      if (this.isSubscriptionTrialRunning) {
        return 10;
      } //Condition for subscription booking
      else if (organizationDoc?.subscriptionDetails) {
        return organizationDoc?.subscriptionDetails.maxUser;
      } // Condition for event based booking
      else {
        if (this.$store.state.draft) {
          if (this.eventType == "video" && this.reqToSpeakMode) {
            return 200;
          } else if (this.eventType == "video" && !this.reqToSpeakMode) {
            return 30;
          } else if (this.eventType == "external" || (this.eventType == "hybrid" && this.hybridType == "audiotrack")) {
            return 2500;
          } else if (
            this.eventType == "livestream" ||
            (this.eventType == "hybrid" && this.hybridType === "livestream")
          ) {
            return 10000;
          } else if (this.eventType == "hybrid" && this.hybridType == "conference") {
            return 30;
          } else {
            return 15;
          }
        } else {
          return 15;
        }
      }
    },
  },
  watch: {
    // discard password if it isn't used
    enablePassword() {
      if (!this.enablePassword) {
        this.moderationPassword = "";
        this.reqToSpeakMode = false;
      }
      // this.checkValidity();
    },
    // enable password if loaded from store
    moderationPassword() {
      if (this.moderationPassword != "" && this.moderationPassword != null) {
        this.enablePassword = true;
      }
      // this.checkValidity();
    },
    valid() {
      this.notValid();
    },
  },
  methods: {
    resetSubEventTypes() {
      this.$store.commit("SET_DRAFT_FIELD", ["hybridType", "livestream"]);
      this.$store.commit("SET_DRAFT_FIELD", ["livestreamType", "player"]);
    },
    isUndefined(variable) {
      if (typeof variable === "undefined") {
        return true;
      }
      return false;
    },
    showParticipants() {
      if (
        (this.eventType === "livestream" && this.livestreamType === "target") ||
        (this.eventType === "hybrid" && this.hybridType === "livestream" && this.livestreamType === "target") ||
        (this.eventType === "hybrid" && this.hybridType === "audiotrack")
      ) {
        return false;
      }
      return true;
    },
    notValid() {
      if (this.name === "") {
        console.error("Event name is empty an empty string");
        this.$refs.nameInput.checkHtml5Validity();
        this.$refs.nameInput.focus();
      }
      if (
        (this.showParticipants() && this.maxParticipants < this.minParticipantsLimit) ||
        (this.showParticipants() && this.maxParticipants > this.maxParticipantsLimit)
      ) {
        console.error("Event participants limit is exceeded");
        this.$refs.maxParticipantsInput.checkHtml5Validity();
        this.$refs.maxParticipantsInput.focus();
      }
      if (this.interpretingParticipants < this.minParticipantsLimit || this.interpretingParticipants > 10000) {
        console.error("Event interpreting participants limit is exceeded");
        this.$refs.interpretingParticipantsInput.checkHtml5Validity();
        this.$refs.interpretingParticipantsInput.focus();
      }
    },
  },
};
</script>

<style></style>
